import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Trash2 } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { ConfirmModal } from "../components/ui/ConfirmModal";
import {
  blacklistsApi,
  type BlacklistDetail,
  type BlacklistDnisOption,
} from "../api/blacklistsApi";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; detail: BlacklistDetail; options: BlacklistDnisOption[] }
  | { status: "error"; message: string };

/**
 * Per-blacklist editor. Mirrors the legacy BlacklistDetails view: pick
 * the DNIS to apply the blacklist to (or leave empty for "all"), set
 * the ANI to block, optionally narrow the blocking period via a date,
 * and either save or delete.
 *
 * The ANI field is read-only when the blacklist was created via the
 * automatic "hidden" route (e.g. via the legacy dial-plan tooling); we
 * surface that as a flag from the API so the user understands why the
 * input is locked.
 */
export function BlacklistEditPage() {
  const { blacklistId: param } = useParams<{ blacklistId: string }>();
  const id = Number(param);
  const navigate = useNavigate();

  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Editable form fields, kept separate from the loaded detail so we can
  // diff/cancel cleanly.
  const [description, setDescription] = useState("");
  const [dnisId, setDnisId] = useState<number | "">("");
  const [aniDisplay, setAniDisplay] = useState("");
  const [blockedTill, setBlockedTill] = useState("");

  useEffect(() => {
    let active = true;
    if (!Number.isFinite(id) || id <= 0) {
      setState({ status: "error", message: "Invalid blacklist id." });
      return;
    }
    Promise.all([blacklistsApi.get(id), blacklistsApi.dnisOptions()])
      .then(([detail, options]) => {
        if (!active) return;
        setState({
          status: "ready",
          detail,
          options: options.items ?? [],
        });
        setDescription(detail.description ?? "");
        setDnisId(detail.dnisId ?? "");
        setAniDisplay(detail.aniDisplay ?? "");
        setBlockedTill(toInputDate(detail.blockedTill));
      })
      .catch((err: unknown) => {
        if (!active) return;
        const message =
          err instanceof Error ? err.message : "Failed to load blacklist";
        setState({ status: "error", message });
      });
    return () => {
      active = false;
    };
  }, [id]);

  const onSave = async () => {
    setError(null);
    setBusy(true);
    try {
      await blacklistsApi.update(id, {
        description,
        dnisId: dnisId === "" ? null : Number(dnisId),
        aniDisplay,
        // Send back as ISO at midnight UTC so the SP receives a real
        // datetime; the legacy portal followed the same shape.
        blockedTill: blockedTill ? `${blockedTill}T00:00:00Z` : new Date().toISOString(),
      });
      navigate("/numbers/blacklists");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save changes");
      setBusy(false);
    }
  };

  const performDelete = async () => {
    setDeleteConfirmOpen(false);
    setError(null);
    setBusy(true);
    try {
      await blacklistsApi.delete(id);
      navigate("/numbers/blacklists");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete blacklist");
      setBusy(false);
    }
  };

  const requestDelete = () => {
    setDeleteConfirmOpen(true);
  };

  const title =
    state.status === "ready" && state.detail.description
      ? state.detail.description
      : "Blacklisted entry";

  return (
    <>
      <PageHeader
        title={title}
        actions={
          <Link
            to="/numbers/blacklists"
            aria-label="Back to Blacklisted"
            className="btn btn-secondary" style={{ display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}
          >
            <ArrowLeft size={14} /> Back to Blacklisted
          </Link>
        }
      />
      <div className="dashboard">
        {state.status === "loading" && (
          <div className="tile" style={{ color: "var(--portal-muted)" }}>
            Loading blacklist…
          </div>
        )}
        {state.status === "error" && (
          <div
            className="tile"
            style={{
              borderColor: "#e74c3c",
              color: "#c0392b",
              background: "#fdecea",
            }}
          >
            <strong>Could not load this blacklist.</strong>
            <div style={{ marginTop: 6, fontSize: "0.9rem" }}>{state.message}</div>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ marginTop: 12 }}
              onClick={() => navigate("/numbers/blacklists")}
            >
              Back
            </button>
          </div>
        )}
        {state.status === "ready" && (
          <section className="tile" style={{ gap: 16, maxWidth: 720 }}>
            {error && (
              <div
                style={{
                  color: "#c0392b",
                  background: "#fdecea",
                  border: "1px solid #e74c3c",
                  borderRadius: 8,
                  padding: 10,
                  fontSize: "0.9rem",
                }}
              >
                {error}
              </div>
            )}

            <Field label="Description">
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={inputStyle}
              />
            </Field>

            <Field label="Number">
              <select
                value={dnisId}
                onChange={(e) =>
                  setDnisId(e.target.value === "" ? "" : Number(e.target.value))
                }
                style={inputStyle}
              >
                <option value="">All numbers</option>
                {state.options.map((o) => (
                  <option key={o.dnisId} value={o.dnisId}>
                    {o.dnisPresentation}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label="ANI"
              hint={
                state.detail.isHidden
                  ? "ANI is locked because this blacklist was created automatically."
                  : undefined
              }
            >
              <input
                type="text"
                value={aniDisplay}
                onChange={(e) => setAniDisplay(e.target.value)}
                readOnly={state.detail.isHidden}
                style={{
                  ...inputStyle,
                  background: state.detail.isHidden
                    ? "var(--portal-surface)"
                    : "white",
                }}
              />
            </Field>

            <Field label="Blocked till">
              <input
                type="date"
                value={blockedTill}
                onChange={(e) => setBlockedTill(e.target.value)}
                style={inputStyle}
              />
            </Field>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
                marginTop: 4,
              }}
            >
              <button
                type="button"
                className="btn btn-secondary"
                onClick={requestDelete}
                disabled={busy}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: "#fdecea",
                  borderColor: "#e74c3c",
                  color: "#c0392b",
                }}
              >
                <Trash2 size={14} /> Delete
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={onSave}
                disabled={busy}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Save size={16} /> {busy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </section>
        )}
      </div>

      {state.status === "ready" && (
        <ConfirmModal
          open={deleteConfirmOpen}
          onClose={() => {
            if (!busy) setDeleteConfirmOpen(false);
          }}
          title="Delete blacklist entry?"
          confirmLabel="Delete"
          tone="danger"
          busy={busy}
          onConfirm={() => void performDelete()}
        >
          Delete this blacklist entry? This cannot be undone.
        </ConfirmModal>
      )}
    </>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          color: "var(--portal-muted)",
          fontSize: "0.8rem",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
      {hint && (
        <div
          style={{
            color: "var(--portal-muted)",
            fontSize: "0.75rem",
            marginTop: 4,
          }}
        >
          {hint}
        </div>
      )}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 12px",
  border: "1px solid var(--portal-border)",
  borderRadius: 6,
  fontSize: "0.95rem",
  fontFamily: "inherit",
  background: "white",
};

function toInputDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  // Use UTC components since the SP stores UTC dates and we want the
  // user to see exactly what is in the database, not a local-time
  // shift across timezones.
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
